import { Image } from '@/components/Analysis/shared/Image';
import { CustomSkeleton } from '@/components/Analysis/shared/Image/styles';
import { urlToBase64 } from '@/utils';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { getPoiCoordinates } from '@/utils/pointOfInterest';
import { useEffect, useMemo, useState } from 'react';

type PointOfInterestDeckInfoProps = {
  imageDimensions: [number, number] | number[];
  resourcePath?: string | null;
  cadTransform?: [number, number, number, number, number, number] | number[];
  centerPoint3d: { x: number; y: number; z: number } | null | undefined;
  handleError?: (error: boolean) => void;
};

export const PointOfInterestDeckInfo = ({
  imageDimensions,
  resourcePath,
  cadTransform,
  centerPoint3d,
  handleError = () => {},
}: PointOfInterestDeckInfoProps) => {
  const [base64Image, setBase64Image] = useState('');
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function getResourceBase64() {
      if (resourcePath) {
        setIsLoading(true);
        const base64 = await urlToBase64(getCloudfrontUrl(resourcePath));
        setBase64Image(base64);
        setHasError(!base64.startsWith('data:image'));
        setIsLoading(false);
      }
    }
    getResourceBase64();
  }, [resourcePath]);

  const coordinates = useMemo(
    () => getPoiCoordinates(centerPoint3d, cadTransform),
    [cadTransform, centerPoint3d]
  );

  if (!coordinates) return null;

  if (isLoading) {
    return <CustomSkeleton animation="wave" />;
  }

  if (hasError) {
    return (
      <Image
        src={base64Image}
        sx={{ width: '100%', height: '100%' }}
        alt="deck-plan"
        onLoadEnd={handleError}
      />
    );
  }

  return (
    <svg
      style={{ position: 'relative', left: 0, top: 0, width: '100%', height: '100%' }}
      viewBox={`0 0 ${imageDimensions[0]} ${imageDimensions[1]}`}
    >
      <image href={base64Image} width="100%" height="100%" />
      <circle cx={coordinates.cx} cy={coordinates.cy} r="20" fill="red" />
    </svg>
  );
};
